import {useCallback, useEffect, useEffectEvent, useState, type ComponentProps} from 'react';
import {cn} from '@/lib/utils';
import {useUiStore} from '@/store/uiStore';
import {usePlatformImageSrc} from '@/components/shared/usePlatformImageSrc';

// Plain constants shared with the inline style below: Tailwind can't read a JS constant, so
// FADE_DURATION_MS and `duration-50` must change together.
const FADE_DURATION_MS = 50;
// Composes with `blur-lg`: Tailwind builds one `filter` from both.
const GREYSCALE_CLASS = 'grayscale';

// How far outside the viewport a lazy thumbnail starts its stored-copy lookup, so it is usually
// resolved by the time it scrolls into view. Close to what browsers use for `loading="lazy"` itself.
const NEAR_VIEWPORT_MARGIN = '1000px';

/**
Tracks whether one piece of platform content should render blurred and greyscale, and exposes
`reveal()` / `colourise()` to clear each independently.

A reveal or colourise is permanent only until `resetKey` changes (pass the video/item id or src, so
advancing to the next item in a queue re-applies both) or the analyst turns that preference back on
after turning it off (so re-enabling always starts protected, rather than leaving whatever was
cleared earlier in the session exposed).
*/
export function useThumbnailReveal(resetKey: unknown): {
  blurThumbnails: boolean;
  greyscaleThumbnails: boolean;
  blurred: boolean;
  greyed: boolean;
  reveal: () => void;
  hide: () => void;
  colourise: () => void;
  greyscale: () => void;
} {
  const blurThumbnails = useUiStore((state) => state.blurThumbnails);
  const greyscaleThumbnails = useUiStore((state) => state.greyscaleThumbnails);
  // Per-media overrides of the page preferences: null follows the preference, a boolean is the
  // analyst's own choice for this media (so blur or greyscale can be applied where the page has it off).
  const [blurOverride, setBlurOverride] = useState<boolean | null>(null);
  const [greyOverride, setGreyOverride] = useState<boolean | null>(null);
  const [trackedKey, setTrackedKey] = useState(resetKey);
  const [trackedBlur, setTrackedBlur] = useState(blurThumbnails);
  const [trackedGrey, setTrackedGrey] = useState(greyscaleThumbnails);

  if (resetKey !== trackedKey) {
    setTrackedKey(resetKey);
    setBlurOverride(null);
    setGreyOverride(null);
  }

  if (blurThumbnails !== trackedBlur) {
    setTrackedBlur(blurThumbnails);
    setBlurOverride(null);
  }

  if (greyscaleThumbnails !== trackedGrey) {
    setTrackedGrey(greyscaleThumbnails);
    setGreyOverride(null);
  }

  return {
    blurThumbnails,
    greyscaleThumbnails,
    blurred: blurOverride ?? blurThumbnails,
    greyed: greyOverride ?? greyscaleThumbnails,
    reveal() {
      setBlurOverride(false);
    },
    // Lets a caller-driven "blur/unblur" control re-blur what `reveal()` opened, or blur media the
    // page preference leaves clear.
    hide() {
      setBlurOverride(true);
    },
    colourise() {
      setGreyOverride(false);
    },
    greyscale() {
      setGreyOverride(true);
    }
  };
}

/**
 Whether a `Thumbnail` for `src` has nothing to show: no stored copy, or one that won't render (the
 platform URL is never loaded instead). A channel or profile picture uses it to show the initial
 instead of an empty frame. Keyed by `src`, so a new URL gets its own chance to load.
 */
export function useThumbnailLoadFailed(src: string | null | undefined) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  return {
    failed: Boolean(src) && failedSrc === src,
    onError() {
      setFailedSrc(src ?? null);
    }
  };
}

/**
Whether `node` has come within `NEAR_VIEWPORT_MARGIN` of the viewport, latched once it has. With
`enabled` false, or where `IntersectionObserver` is unavailable, it is near from the start.
*/
function useNearViewport(node: Element | null, enabled: boolean): boolean {
  const [near, setNear] = useState(!enabled || typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    if (near || !node) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true);
        }
      },
      {rootMargin: NEAR_VIEWPORT_MARGIN}
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [near, node]);

  return near;
}

/**
Drop-in replacement for `<img>` for any platform-content thumbnail (video, channel avatar, item
preview). Blurred and greyscaled per the analyst's preferences. Nothing about the pointer lifts
either filter - not hovering, not clicking the image - so content is never shown by accident; only
an explicit control (see `SensitiveMedia`) does, through `toggleBlurRef` / `toggleGreyscaleRef`.
*/
export function Thumbnail({
  alt,
  className,
  style,
  onBlurredChange,
  onGreyedChange,
  toggleBlurRef,
  toggleGreyscaleRef,
  storedSha256,
  onError,
  onLoad,
  ref,
  ...props
}: Omit<ComponentProps<'img'>, 'onError'> & {
  // The hash of our stored copy of this image, when the caller knows it; otherwise the copy is looked
  // up by `src`. Only the stored copy is ever shown, never the platform URL (see usePlatformImageSrc).
  readonly storedSha256?: string | null | undefined;
  // Called when there is nothing to show: no stored copy, or one that won't render.
  readonly onError?: () => void;
  // Notified whenever this thumbnail's own blurred state changes, so a caller (e.g. a hover pill
  // button) can mirror it without keeping a second, independently-keyed reveal state of its own.
  readonly onBlurredChange?: (blurred: boolean) => void;
  // A caller-owned ref this component fills in with a toggle function, so e.g. a hover pill button
  // can blur/unblur this exact thumbnail without duplicating its reveal/hide state.
  readonly toggleBlurRef?: {current: (() => void) | null};
  // The greyscale counterparts of `onBlurredChange` and `toggleBlurRef`.
  readonly onGreyedChange?: (greyed: boolean) => void;
  readonly toggleGreyscaleRef?: {current: (() => void) | null};
}) {
  const {blurThumbnails, greyscaleThumbnails, blurred, greyed, reveal, hide, colourise, greyscale} =
    useThumbnailReveal(props.src);
  const [node, setNode] = useState<HTMLImageElement | null>(null);
  // The lookup is a fetch, so the browser's own `loading="lazy"` can't defer it: a lazy thumbnail
  // waits until it nears the viewport, or a page of cards would request every image up front.
  const near = useNearViewport(node, props.loading === 'lazy');
  const setRef = useCallback(
    (element: HTMLImageElement | null) => {
      setNode(element);
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    },
    [ref]
  );
  const image = usePlatformImageSrc(near ? props.src : undefined, storedSha256);
  const reportUnavailable = useEffectEvent(() => {
    onError?.();
  });
  useEffect(() => {
    if (image.missing) {
      reportUnavailable();
    }
  }, [image.missing]);
  // Kept fresh every render (rather than written during render, which the refs lint rule forbids)
  // via an unconditional, no-dependency-array effect - the cheapest way to keep this function
  // current without imposing `useImperativeHandle`'s ref-object-with-methods shape on callers that
  // just want a plain ref cell.
  useEffect(() => {
    if (toggleBlurRef) {
      toggleBlurRef.current = () => {
        if (blurred) {
          reveal();
        } else {
          hide();
        }
      };
    }
  });

  // Same pattern as the blur toggle above.
  useEffect(() => {
    if (toggleGreyscaleRef) {
      toggleGreyscaleRef.current = () => {
        if (greyed) {
          colourise();
        } else {
          greyscale();
        }
      };
    }
  });

  useEffect(() => {
    onGreyedChange?.(greyed);
  }, [greyed, onGreyedChange]);

  useEffect(() => {
    onBlurredChange?.(blurred);
  }, [blurred, onBlurredChange]);

  return (
    <img
      {...props}
      ref={setRef}
      src={image.src}
      alt={alt}
      // Never a platform URL (see usePlatformImageSrc), but a same-origin or caller-supplied src still
      // has no reason to name the page it was shown on.
      referrerPolicy="no-referrer"
      className={cn(blurred && 'blur-lg', greyed && GREYSCALE_CLASS, className)}
      // Transition set inline, not as a Tailwind class, and only while a filter is in play:
      // tailwind-merge treats any `transition-*` utility as one conflict group, so a caller's own
      // `transition-transform` (several cards use one for a hover-zoom effect) would silently drop
      // ours, and the blur would snap instead of fading. Inline style can't be merged away by a
      // className - but applying it unconditionally would itself permanently override that same
      // caller transition even with both preferences off, so it's gated on them. Both
      // `filter` and `transform` are listed so a caller's own transform transition (the hover-zoom
      // above) keeps animating too, instead of losing its transition-property to this one.
      style={
        blurThumbnails || greyscaleThumbnails || blurred || greyed
          ? {
              transitionProperty: 'filter, transform',
              transitionDuration: `${FADE_DURATION_MS}ms`,
              ...style
            }
          : style
      }
      onError={() => {
        onError?.();
      }}
      onLoad={onLoad}
    />
  );
}
